// api.js
import { getUserFields } from "components/user/config";
import { getItemsFromCollection, getItemsCount } from "directus/src/operations";

function formatDate(dateString) {
  if (!dateString) return "";
  const d = new Date(dateString);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = String(d.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

export async function getCaseStudyDetails({ id }) {
  try {
    const query = {
      filter: { id: { _eq: id } },
      fields: [
        "*",
        "outcomes.type",
        "outcomes.description",
        "deliverables.name",
        "deliverables.url",
        "project.id",
        getUserFields("project.coordinator"),
        "name",
        "user_created.id",
        "user_created.first_name",
        "user_created.last_name",
        "user_created.avatar",
        "project.name",
        "project.project_id",
        "project.description",
        "industry_v1.name",
        "industry_v1.id",
        "industry_v1.type",
        "nda_level",
        "industry",
        "project_name_alias",
        "screenshots.screenshot",
        "testimonials.feedback",
        "tools.tool.type",
        "tools.tool.name",
        "tech_stack.tech_stack.type",
        "tech_stack.tech_stack.name",
        "video",
      ],
      limit: 1,
    };
    const [result] = await getItemsFromCollection("case_studies", query);
    if (!result) return null;

    // Prepare data (same as your existing transform logic)
    const outcomesFeatures = [];
    const outcomesHighlights = [];
    (result.outcomes || []).forEach((o) => {
      if (o.type === "features") outcomesFeatures.push(o.description);
      else if (o.type === "project_highlights")
        outcomesHighlights.push(o.description);
    });

    const deliverables = (result.deliverables || []).map((d) => ({
      deliverable: d.name,
      link: d.url,
    }));

    const tools = (result.tools || []).map((t) => ({
      name: t.tool?.name || "",
      type: t.tool?.type || "",
    }));

    const techStack = (result.tech_stack || []).map((t) => ({
      name: t.tech_stack?.name || "",
      type: t.tech_stack?.type || "",
    }));

    return {
      id: result.id,
      date_started: formatDate(result.date_started),
      duration: result.duration,
      team_size: result.team_size,
      name: result.name,
      project: {
        name: result.project?.name,
      },
      industry_v1: {
        ...result?.industry_v1,
      },
      alias: result.project_name_alias,
      industry: result.industry,
      nda_level: result.nda_level,
      user: {
        id: result.user_created.id,
        first_name: result.user_created.first_name,
        last_name: result.user_created.last_name,
        avatar: result.user_created.avatar,
      },
      project_name_alias: result.project_name_alias,
      project_coordinator_id: result.project?.coordinator?.id,
      client_name: result.project?.client?.name,
      client_country: result.project?.client?.company_id?.country_id?.name,
      client_about: result.project?.client?.about,
      tag_line: result.tag_line,
      client_requirement: result.client_requirement,
      client_testimonials: (result.testimonials || []).map((t) => t.feedback),
      objective: result.project?.description,
      solution_overview: result.solution_overview,
      outcomes_features: outcomesFeatures.join("\n\n"),
      outcomes_project_highlights: outcomesHighlights.join("\n\n"),
      challenges: result.obstacles,
      achievements: result.achievements,
      methodology: result.methodology,
      tech_stack: techStack,
      tools,
      deliverables_list: deliverables,
      screenshots: (result.screenshots || []).map((s) => s.screenshot),
      video: result.video,
    };
  } catch (err) {
    console.error("Error fetching case study details:", err);
    return null;
  }
}

export async function getCaseStudiesList() {
  try {
    const query = {
      sort: ["id"],
      fields: [
        "id",
        "date_started",
        "name",
        "project",
        "status",
        "industry",
        "nda_level",
        "user_created.id",
        "user_created.first_name",
        "user_created.last_name",
        "user_created.avatar",
      ],
    };
    const data = await getItemsFromCollection("case_studies", query);
    return { data, totalCount: data.length };
  } catch (err) {
    console.error("Error fetching list:", err);
    return { data: [], totalCount: 0 };
  }
}

export async function getCaseStudyNameById(id) {
  try {
    const query = {
      filter: { id: { _eq: id } },
      fields: ["name"],
      limit: 1,
    };
    const [result] = await getItemsFromCollection("case_studies", query);
    return result ? result.name : null;
  } catch (err) {
    console.error("Error fetching case study name:", err);
    return null;
  }
}
